import { Button, Icons, useToggle } from '@commenda/kakashi';
import {
  LegacyDocumentType,
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { ServiceRequestTaskLabel } from '../../../common/labels';
import { CorporationUploadFileModal } from '../../../common/modals/CorporationUploadFileModal';
import { ServiceRequestTaskIconConfig } from '../../../common/taskIconConfig';
import { useServiceRequestFile } from '../../../hook/useServiceRequestFile';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { updateTaskStatus, viewUploadedFile } from '../../../utils';

export const UploadDirectorWorkingInOfficeProofTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;

  const uploadModalVisibility = useToggle();

  const [file, setFile] = useServiceRequestFile(
    serviceRequest,
    LegacyDocumentType.DirectorInOfficeProof
  );

  const handleViewFile = () => viewUploadedFile(file.id);

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label="Client uploads photo of (at least) one director working in
                office"
              />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: file?.id ? (
              <Button size="sm" variant="secondary" onClick={handleViewFile}>
                View upload
              </Button>
            ) : null,
            label: (
              <ServiceRequestTaskLabel
                label="Photo of At Least One Director Working in Office Uploaded"
                timestamp={task.updatedAt}
                status={task.status}
              />
            ),
          },
        },
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={uploadModalVisibility.show}
                variant="primary"
              >
                <Icons.Upload01 className="size-4" />
                Upload
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload Photo of At Least One Director Working in Office" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled variant="primary">
                <Icons.Upload01 className="size-4" />
                Upload
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload Photo of At Least One Director Working in Office" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: file?.id ? (
              <Button size="sm" variant="secondary" onClick={handleViewFile}>
                View upload
              </Button>
            ) : null,
            label: (
              <ServiceRequestTaskLabel
                label="Photo of At Least One Director Working in Office Uploaded"
                timestamp={task.updatedAt}
                status={task.status}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />

      <CorporationUploadFileModal
        serviceRequestTaskId={task.id}
        isOpen={uploadModalVisibility.visible}
        setIsOpen={uploadModalVisibility.hide}
        file={file}
        setFile={setFile}
        heading="Upload Photo of At Least One Director Working in Office"
        documentType={LegacyDocumentType.DirectorInOfficeProof}
        serviceRequest={serviceRequest}
        onUploadSuccess={async () => {
          await updateTaskStatus({
            task,
            status: TaskStatus.COMPLETED,
            mutate,
          });
        }}
      />
    </div>
  );
};
